import React from "react";

import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { RegionSearchState } from "./types";

interface RegionSearchResultsProps {
  region: string;
  searchState: RegionSearchState;
  sourceOrgId: string;
  sourceRegionCode: string;
  onCopy: (targetOrgId: string) => void;
}

export const RegionSearchResults: React.FC<RegionSearchResultsProps> = ({
  region,
  searchState,
  sourceOrgId,
  sourceRegionCode,
  onCopy,
}) => {
  const { isLoading, roles, copyStatus } = searchState;
  const hasResults = roles.length > 0;

  if (isLoading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <CircularProgress size={20} />
          <Typography>
            Searching organizations in {region.toUpperCase()}...
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (!hasResults) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="text.secondary">
          No organizations found in {region.toUpperCase()}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
        <Typography variant="h6">
          {region.toUpperCase()} search results
        </Typography>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Organization Name</TableCell>
              <TableCell>Organization ID</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map((role) => {
              const status = copyStatus[role.id] || {
                isLoading: false,
                isSuccess: false,
              };
              const isSameOrg =
                role.id === sourceOrgId && region === sourceRegionCode;

              return (
                <TableRow key={role.id}>
                  <TableCell>{role.name}</TableCell>
                  <TableCell>{role.id}</TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => onCopy(role.id)}
                      disabled={
                        isSameOrg || status.isLoading || status.isSuccess
                      }
                      startIcon={
                        status.isLoading ? <CircularProgress size={20} /> : null
                      }
                      color={status.isSuccess ? "success" : "primary"}
                    >
                      {status.isSuccess
                        ? "Copied"
                        : "Copy course to this organisation"}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};
